import { type SalesMetricV2 } from "../types";

export function makeRevLtv({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revLtv";

  return {
    id,
    name: isDashboardV2 ? "Revenue (∞)" : "LTV Attributed Rev",
    fullName: "Revenue / All (∞)",
    description: `Revenue attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevFtLtv({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revFtLtv";

  return {
    id,
    name: isDashboardV2 ? "Revenue (∞, New)" : "LTV Attributed Rev (1st time)",
    fullName: "Revenue / New (∞)",
    description: `Revenue attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevRtnLtv({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revRtnLtv";

  return {
    id,
    name: isDashboardV2
      ? "Revenue (∞, Returning)"
      : "LTV Attributed Rev (Returning)",
    fullName: "Revenue / Ret (∞)",
    description: `Revenue attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTxnsLtv({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "txnsLtv";

  return {
    id,
    name: isDashboardV2 ? "Orders (∞)" : "LTV Transactions",
    fullName: "Orders / All (∞)",
    description: `Transactions attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTxnsFtLtv({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "txnsFtLtv";

  return {
    id,
    name: isDashboardV2 ? "Orders (∞, New)" : "LTV Transactions (1st time)",
    fullName: "Orders / New (∞)",
    description: `Transactions attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTxnsRtnLtv({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "txnsRtnLtv";

  return {
    id,
    name: isDashboardV2
      ? "Orders (∞, Returning)"
      : "LTV Transactions (Returning)",
    fullName: "Orders / Ret (∞)",
    description: `Transactions attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueWebShopsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueWebShopsLtv";

  return {
    id,
    name: isDashboardV2 ? "Web Revenue (∞)" : "LTV Web Revenue",
    fullName: "Web Revenue / All (∞) ",
    description: "Revenue attributed to your ads in Web shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueMetaShopsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueMetaShopsLtv";

  return {
    id,
    name: isDashboardV2 ? "Meta Revenue (∞)" : "LTV Meta Shops Revenue",
    fullName: "Meta Revenue / All (∞)",
    description: "Revenue attributed to your ads in Meta shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueTikTokShopsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueTikTokShopsLtv";

  return {
    id,
    name: isDashboardV2 ? "TikTok Revenue (∞)" : "LTV TikTok Shops Revenue",
    fullName: "TikTok Revenue / All (∞)",
    description: "Revenue attributed to your ads in TikTok shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsWebShopsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsWebShopsLtv";

  return {
    id,
    name: isDashboardV2 ? "Web Orders (∞)" : "LTV Web Transactions",
    fullName: "Web Orders / All (∞)",
    description: "Orders attributed to your ads in Web shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsMetaShopsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsMetaShopsLtv";

  return {
    id,
    name: isDashboardV2 ? "Meta Orders (∞)" : "LTV Meta Shops Transactions",
    fullName: "Meta Orders / All (∞)",
    description: "Orders attributed to your ads in Meta shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsTikTokShopsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsTikTokShopsLtv";

  return {
    id,
    name: isDashboardV2 ? "TikTok Orders (∞)" : "LTV TikTok Shops Transactions",
    fullName: "TikTok Orders / All (∞)",
    description: "Orders attributed to your ads in TikTok shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsLtv";

  return {
    id,
    name: isDashboardV2 ? "Discounts (∞)" : "LTV Discounts",
    fullName: "Discounts / All (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to " +
      "orders (or PROMOS if this applied to Amazon) as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsFtLtv";

  return {
    id,
    name: isDashboardV2 ? "Discounts (∞, New)" : "LTV Discounts (1st time)",
    fullName: "Discounts / New (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to first time " +
      "customer orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsRtnLtv";

  return {
    id,
    name: isDashboardV2
      ? "Discounts (∞, Returning)"
      : "LTV Discounts (Returning)",
    fullName: "Discounts / Ret (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to returning " +
      "customer orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueLtv";

  return {
    id,
    name: isDashboardV2 ? "Tax (∞)" : "LTV Tax",
    fullName: "Tax / All (∞)",
    description:
      "The sum of all the taxes applied to orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueFtLtv";

  return {
    id,
    name: isDashboardV2 ? "Tax (∞, New)" : "LTV Tax (1st time)",
    fullName: "Tax / New (∞)",
    description:
      "The sum of all the taxes applied to first time customer " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueRtnLtv";

  return {
    id,
    name: isDashboardV2 ? "Tax (∞, Returning)" : "LTV Tax (Returning)",
    fullName: "Tax / Ret (∞)",
    description:
      "The sum of all the taxes applied to returning customer " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueLtv";

  return {
    id,
    name: isDashboardV2 ? "Customer Shipping (∞)" : "LTV Customer Shipping",
    fullName: "Customer Shipping / All (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueFtLtv";

  return {
    id,
    name: isDashboardV2
      ? "Customer Shipping (∞, New)"
      : "LTV Customer Shipping (1st time)",
    fullName: "Customer Shipping / New (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from first time customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueRtnLtv";

  return {
    id,
    name: isDashboardV2
      ? "Customer Shipping (∞, Returning)"
      : "LTV Customer Shipping (Returning)",
    fullName: "Customer Shipping / Ret (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from returning customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueLtv";

  return {
    id,
    name: isDashboardV2 ? "Refunds (∞)" : "LTV Refunds",
    fullName: "Refunds / All (∞)",
    description:
      "Returns, refunds, AND cancellations from " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueFtLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueFtLtv";

  return {
    id,
    name: isDashboardV2 ? "Refunds (∞, New)" : "LTV Refunds (1st time)",
    fullName: "Refunds / New (∞)",
    description:
      "Returns, refunds, AND cancellations from " +
      "first time customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueRtnLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueRtnLtv";

  return {
    id,
    name: isDashboardV2 ? "Refunds (∞, Returning)" : "LTV Refunds (Returning)",
    fullName: "Refunds / Ret (∞)",
    description:
      "Returns, refunds, AND cancellations from " +
      "returning customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueOrderSubscriptionLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (∞)"
      : "LTV Subscription attributed revenue",
    fullName: "Subscription Revenue / All (∞)",
    description: "Includes all subscription categories",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueOrderSubscriptionFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (∞, New)"
      : "LTV Subscription attributed revenue (1st time)",
    fullName: "Subscription Revenue / New (∞)",
    description:
      "Includes order tags you've mapped to the 'first time' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueOrderSubscriptionRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (∞, Recurring)"
      : "LTV Subscription attributed revenue (Recurring)",
    fullName: "Subscription Revenue / Recurring (∞)",
    description:
      "Includes order tags you've mapped to the 'recurring' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeRevenueOrderSubscriptionOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (∞, Other)"
      : "LTV Subscription attributed revenue (Other)",
    fullName: "Subscription Revenue / Other (∞)",
    description:
      "Includes all order tags that were mapped to 'other', or could not be mapped to " +
      "'recurring' or 'first time'",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsOrderSubscriptionLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (∞)"
      : "LTV Subscription transactions",
    fullName: "Subscription Orders / All (∞)",
    description: "Includes all subscription categories",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsOrderSubscriptionFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (∞, New)"
      : "LTV Subscription transactions (1st time)",
    fullName: "Subscription Orders / New (∞)",
    description:
      "Includes order tags you've mapped to the 'first time' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsOrderSubscriptionRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (∞, Recurring)"
      : "LTV Subscription transactions (Recurring)",
    fullName: "Subscription Orders / Recurring (∞)",
    description:
      "Includes order tags you've mapped to the 'recurring' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTransactionsOrderSubscriptionOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (∞, Other)"
      : "LTV Subscription transactions (Other)",
    fullName: "Subscription Orders / Other (∞)",
    description:
      "Includes all order tags that were mapped to 'other', or could not be mapped to " +
      "'recurring' or 'first time'",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsOrderSubscriptionsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Discounts (∞)"
      : "LTV Subscription Discounts",
    fullName: "Subscription Discounts / All (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to subscription " +
      "orders (or PROMOS if this applied to Amazon) as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsOrderSubscriptionsFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Discounts (∞, New)"
      : "LTV Subscription Discounts (1st time)",
    fullName: "Subscription Discounts / New (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to first time customer " +
      "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsOrderSubscriptionsRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Discounts (∞, Recurring)"
      : "LTV Subscription Discounts (Recurring)",
    fullName: "Subscription Discounts / Recurring (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to recurring customer " +
      "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderDiscountsOrderSubscriptionsOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Discounts (∞, Other)"
      : "LTV Subscription Discounts (Other)",
    fullName: "Subscription Discounts / Other (∞)",
    description:
      "Value of all discount codes and automatic discounts applied to other customer " +
      "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueOrderSubscriptionsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsLtv";

  return {
    id,
    name: isDashboardV2 ? "Subscription Tax (∞)" : "LTV Subscription Tax",
    fullName: "Subscription Tax / All (∞)",
    description:
      "The sum of all the taxes applied to subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueOrderSubscriptionsFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Tax (∞, New)"
      : "LTV Subscription Tax (1st time)",
    fullName: "Subscription Tax / New (∞)",
    description:
      "The sum of all the taxes applied to first time customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueOrderSubscriptionsRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Tax (∞, Recurring)"
      : "LTV Subscription Tax (Recurring)",
    fullName: "Subscription Tax / Recurring (∞)",
    description:
      "The sum of all the taxes applied to recurring customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeTaxValueOrderSubscriptionsOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Tax (∞, Other)"
      : "LTV Subscription Tax (Other)",
    fullName: "Subscription Tax / Other (∞)",
    description:
      "The sum of all the taxes applied to other customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueOrderSubscriptionsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsLtv";

  return {
    id,
    name: isDashboardV2
      ? "Customer Subscription Shipping (∞)"
      : "LTV Customer Subscription Shipping",
    fullName: "Customer Subscription Shipping / All (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueOrderSubscriptionsFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Customer Subscription Shipping (∞, New)"
      : "LTV Customer Subscription Shipping (1st time)",
    fullName: "Customer Subscription Shipping / New (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from first time customer subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueOrderSubscriptionsRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Customer Subscription Shipping (∞, Recurring)"
      : "LTV Customer Subscription Shipping (Recurring)",
    fullName: "Customer Subscription Shipping / Recurring (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from recurring customer subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeShippingValueOrderSubscriptionsOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Customer Subscription Shipping (∞, Other)"
      : "LTV Customer Subscription Shipping (Other)",
    fullName: "Customer Subscription Shipping / Other (∞)",
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from other customer subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueOrderSubscriptionsLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Refunds (∞)"
      : "LTV Subscription Refunds",
    fullName: "Subscription Refunds / All (∞)",
    description:
      "Returns, refunds, AND cancellations from subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueOrderSubscriptionsFirstTimeLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsFirstTimeLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Refunds (∞, New)"
      : "LTV Subscription Refunds (1st time)",
    fullName: "Subscription Refunds / New (∞)",
    description:
      "Returns, refunds, AND cancellations from first time customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueOrderSubscriptionsRecurringLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsRecurringLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Refunds (∞, Recurring)"
      : "LTV Subscription Refunds (Recurring)",
    fullName: "Subscription Refunds / Recurring (∞)",
    description:
      "Returns, refunds, AND cancellations from recurring customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}

export function makeOrderRefundedValueOrderSubscriptionsOtherLtv({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsOtherLtv";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Refunds (∞, Other)"
      : "LTV Subscription Refunds (Other)",
    fullName: "Subscription Refunds / Other (∞)",
    description:
      "Returns, refunds, AND cancellations from other customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isLtv: true,
  };
}
